/**
 * Live DOM patching inside the preview iframe.
 *
 * The engine wraps every editable Liquid output with an HTML comment pair:
 *   <!--lc:setting:section.<id>.<name>-->value<!--/lc:setting:section.<id>.<name>-->
 *
 * This module finds those pairs and rewrites what's between them when the
 * user edits a value in the inspector, so the user sees their change in
 * <50ms without a network round-trip.
 *
 * If no marker exists for a given setting (e.g. setting is used inside an
 * HTML attribute or behind a Liquid filter we can't replicate), the caller
 * should fall back to a server re-render.
 */

import { MARKER_PREFIX, MARKER_PREFIX_END } from '../lib/markers';

export interface PatchResult {
  applied: number; // number of marker pairs updated
}

/**
 * Replace every occurrence of `ref` inside the document with `value`.
 * Returns how many pairs were updated (0 means no marker found).
 */
export function patchSettingInDom(
  doc: Document,
  ref: string,
  value: any,
  opts?: { richText?: boolean }
): PatchResult {
  const beginTag = `${MARKER_PREFIX}${ref}`;
  const endTag = `${MARKER_PREFIX_END}${ref}`;

  const walker = doc.createTreeWalker(doc, NodeFilter.SHOW_COMMENT);
  const pairs: Array<{ begin: Comment; end: Comment }> = [];
  let pending: Comment | null = null;

  let node = walker.nextNode() as Comment | null;
  while (node) {
    const text = node.data;
    if (text === beginTag || text.startsWith(beginTag)) {
      pending = node;
    } else if (text === endTag && pending) {
      pairs.push({ begin: pending, end: node });
      pending = null;
    }
    node = walker.nextNode() as Comment | null;
  }

  if (pairs.length === 0) return { applied: 0 };

  const strValue = value == null ? '' : String(value);
  let actuallyApplied = 0;

  for (const { begin, end } of pairs) {
    /*
      Validação crítica: begin e end DEVEM compartilhar o mesmo
      parent. Em sections geradas/customizadas onde o Liquid coloca
      `{{ section.settings.X }}` dentro de estruturas aninhadas
      (ex.: dentro de uma tag que se fecha entre os markers, ou
      em arquivo gerado por IA com markup desbalanceado), os
      markers ficam em parents diferentes no DOM. Sem essa
      checagem, `insertBefore(node, end)` lança `NotFoundError`
      porque `end` não é child de `begin.parentNode`.

      O erro era fatal: matava toda a chain de live-patch da
      subscription do store, deixando subsequentes mudanças sem
      efeito até reload da página.

      User reportou: criou section nova via IA, settings não
      refletiam no preview. Console: "NotFoundError: Failed to
      execute 'insertBefore' on 'Node'".

      Fix: skip silencioso do par órfão. Caller cai em re-render
      completo da section (já existe esse fallback).
    */
    if (!begin.parentNode || begin.parentNode !== end.parentNode) {
      console.warn(
        '[dom-patcher] marker pair has mismatched parents — skipping. ' +
          `ref="${ref}". Section liquid provavelmente tem ` +
          '`{{ section.settings.X }}` dentro de estrutura aninhada ' +
          'que separa os markers. Fallback: re-render server-side.'
      );
      continue;
    }

    // Remove every node strictly between begin and end
    let cur = begin.nextSibling;
    while (cur && cur !== end) {
      const next = cur.nextSibling;
      cur.remove();
      cur = next;
    }
    try {
      if (opts?.richText) {
        // Parse HTML fragment and insert
        const template = doc.createElement('template');
        template.innerHTML = strValue;
        begin.parentNode.insertBefore(template.content, end);
      } else {
        begin.parentNode.insertBefore(doc.createTextNode(strValue), end);
      }
      actuallyApplied++;
    } catch (err) {
      /*
        Defesa de segunda camada: se mesmo com parents iguais o
        insertBefore falhar (ex.: DOM mutado por outro script no
        intervalo entre nossa checagem e a chamada), capturamos
        sem propagar pra subscription. Caller cai em re-render.
      */
      console.warn(
        '[dom-patcher] insertBefore falhou apesar de parents iguais — ' +
          `ref="${ref}". Erro:`,
        err
      );
    }
  }

  return { applied: actuallyApplied };
}

/**
 * Update a CSS variable on the section root element.
 * Used for COLOR / font / spacing settings exposed via CSS vars.
 *
 * `!important` é necessário porque os <style> blocks da section
 * declaram a mesma var no mesmo seletor (`#section-X { --foo: ... }`).
 * Sem important a regra inline (specificity 1,0,0,0,0) deveria ganhar
 * da regra do <style> block (0,1,0,0), MAS o cascade de custom
 * properties comporta-se de forma diferente em alguns browsers/devtools
 * — empiricamente o inline aparecia "riscado" e o <style> block vencia.
 * Forçar important no setProperty resolve sem precisar tocar no
 * theme.
 */
export function patchCssVar(doc: Document, sectionId: string, varName: string, value: string): boolean {
  const el = doc.querySelector(`#section-${CSS.escape(sectionId)}`) as HTMLElement | null;
  if (!el) return false;
  const cssName = varName.startsWith('--') ? varName : `--${varName.replace(/_/g, '-')}`;
  el.style.setProperty(cssName, value, 'important');
  return true;
}

/**
 * Patcha uma CSS var no `documentElement` (root). Usado em sections
 * "config-only" (tipo cart-drawer-config) que declaram a var num bloco
 * `:root { ... }` no `<style>` da própria section. O markup que CONSOME
 * a var (ex.: snippet `drawer-cart`) renderiza FORA do `#section-<id>`,
 * então `patchCssVar` (que mira só o wrapper da section) não tem efeito
 * visual. Setar `documentElement.style.setProperty(...)` com `!important`
 * sobrescreve o `:root` rule legado e propaga pra todos os descendentes
 * que não têm scope mais específico.
 *
 * Sections que declaram a var só em `#section-<id>` (sem `:root`) NÃO
 * devem chamar essa função — patcha root só pra vars verdadeiramente
 * "globais" pra evitar leak entre sections.
 */
export function patchCssVarRoot(doc: Document, varName: string, value: string): boolean {
  const root = doc.documentElement;
  if (!root) return false;
  const cssName = varName.startsWith('--') ? varName : `--${varName.replace(/_/g, '-')}`;
  root.style.setProperty(cssName, value, 'important');
  return true;
}

/**
 * Replace the whole outerHTML of a section with a freshly rendered piece.
 * Dispatches theme:section:unload / theme:section:load around the swap
 * so theme JS can clean up & re-initialize.
 *
 * We specifically target an element whose data-section-id matches — this
 * avoids hitting a nested `<section id="…">` that some themes emit with
 * the same id as the outer wrapper (duplicate-id anti-pattern).
 *
 * `opts.reveal` defaults to FALSE — settings edits that fall through to a
 * server re-render would otherwise flash the section on every keystroke of
 * a color picker / range drag. Callers adding a brand-new section
 * (insertSectionHtml handles this automatically) or explicitly wanting a
 * visual cue pass `reveal: true`.
 */
export function replaceSectionHtml(
  doc: Document,
  sectionId: string,
  html: string,
  opts?: { reveal?: boolean }
): boolean {
  const escaped = (window as any).CSS?.escape
    ? CSS.escape(sectionId)
    : sectionId.replace(/"/g, '\\"');
  const el =
    (doc.querySelector(`[data-section-id="${escaped}"]`) as HTMLElement | null) ||
    (doc.getElementById(`section-${sectionId}`) as HTMLElement | null);
  if (!el) return false;

  //
  // Scroll preservation — antes o usuário editava uma setting no
  // Inspector e o preview pulava "um pouco pra baixo" sem razão
  // aparente. Causa: os <script> inline da section (main-product,
  // featured-product, etc.) têm DOMContentLoaded/init handlers que,
  // ao re-executar pós-replace, chamam scrollIntoView pra focar em
  // elementos internos. A section em si também muda de altura quando
  // o lojista ajusta font-size — se o conteúdo acima do fold crescer,
  // o scroll fica "fora de lugar".
  //
  // Solução: fazer snapshot do scrollY do iframe ANTES do replace e
  // restaurar DEPOIS dos scripts rodarem. Se algum handler tentou
  // scrollar, a gente desfaz na sequência. `requestAnimationFrame`
  // garante que o restore roda num frame depois da re-pintura.
  //
  const view = doc.defaultView;
  const savedScrollY = view?.scrollY ?? 0;
  const savedScrollX = view?.scrollX ?? 0;

  doc.defaultView?.dispatchEvent(
    new CustomEvent('theme:section:unload', { detail: { sectionId } })
  );

  if (!html || typeof html !== 'string') {
    console.warn('[preview] replaceSectionHtml: empty html for sectionId', sectionId);
    return false;
  }
  const tpl = doc.createElement('template');
  tpl.innerHTML = html.trim();
  const fragment = (tpl.content as DocumentFragment | null) || tpl;
  const newEl = (fragment as any).firstElementChild as HTMLElement | null;
  if (!newEl) {
    console.warn('[preview] replaceSectionHtml: html parsed but no element', { sectionId, htmlLen: html.length });
    return false;
  }
  el.replaceWith(newEl);

  doc.defaultView?.dispatchEvent(
    new CustomEvent('theme:section:load', {
      detail: { sectionId, sectionEl: newEl },
    })
  );
  runEmbeddedScripts(newEl);
  if (opts?.reveal === true) {
    revealSection(doc, newEl);
  } else if (view) {
    // Only restore when not explicitly revealing (reveal intentionally
    // scrolls to show the user the change). Double RAF covers the case
    // where an embedded script schedules its own scroll in the next
    // microtask — we overwrite it on the following frame.
    const restore = () => view.scrollTo(savedScrollX, savedScrollY);
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }
  return true;
}

/**
 * Insert a freshly rendered section at `index` of the given group root.
 * Groups map to containers by data-section-group attribute in the layout.
 *
 * After insertion the new section is smoothly scrolled into view and
 * briefly highlighted so the user can see where it landed.
 */
export function insertSectionHtml(
  doc: Document,
  group: string,
  html: string,
  index: number,
  opts?: { reveal?: boolean }
): boolean {
  /*
    Defesa: html pode vir vazio/undefined quando a engine falha em
    renderizar (ex.: section type novo num grupo que ainda não tem
    um container parent no layout, como `cart-drawer-group` que vive
    DENTRO do snippet drawer-cart). Sem o guard, `html.trim()` ou o
    `tpl.content.firstElementChild` explodia com "Cannot read
    properties of null" em produção.
  */
  if (!html || typeof html !== 'string') {
    console.warn('[preview] insertSectionHtml: empty html for group', group);
    return false;
  }

  // Find siblings belonging to the same group, pick insertion point
  const siblings = Array.from(
    doc.querySelectorAll(`[data-section-group="${group}"]`)
  ) as HTMLElement[];

  const tpl = doc.createElement('template');
  tpl.innerHTML = html.trim();
  // Em browsers que suportam <template> (todos os modernos),
  // `tpl.content` é sempre um DocumentFragment. Mas mantemos o
  // fallback defensivo — descobrimos casos onde algum polyfill ou
  // bundler quirk deixava `content` undefined em iframes específicos.
  const fragment = (tpl.content as DocumentFragment | null) || tpl;
  const newEl = (fragment as any).firstElementChild as HTMLElement | null;
  if (!newEl) {
    console.warn('[preview] insertSectionHtml: html parsed but no element', { group, htmlLen: html.length });
    return false;
  }

  /*
    `cart-drawer-group` vive DENTRO do snippet `drawer-cart`, dentro
    de `<div class="cart-drawer-extra-content">`. Quando o user adiciona
    a PRIMEIRA section custom ao grupo (ainda só tem o `drawer-config`
    config-only que não emite markup visível), o seletor por
    `data-section-group` pode achar SÓ um elemento com markup vazio —
    inserir pelo nextSibling funciona. Mas se NENHUMA section foi
    renderizada ainda (siblings vazio), cair em `body.appendChild` é
    errado: o markup vai parar fora do drawer e o user não vê.

    Estratégia: tenta achar o container `.cart-drawer-extra-content`
    explícito. Se existir, append nele. Senão fallback pra body
    (legado pra outros groups).
  */
  if (siblings.length === 0) {
    if (group === 'cart-drawer-group') {
      const container = doc.querySelector('.cart-drawer-extra-content') as HTMLElement | null;
      if (container) {
        container.appendChild(newEl);
      } else {
        console.warn('[preview] cart-drawer-extra-content container not found, appending to body as fallback');
        doc.body.appendChild(newEl);
      }
    } else {
      // First section of this group — append to body
      doc.body.appendChild(newEl);
    }
  } else if (index >= siblings.length) {
    const last = siblings[siblings.length - 1];
    last.parentNode?.insertBefore(newEl, last.nextSibling);
  } else {
    const ref = siblings[index];
    ref.parentNode?.insertBefore(newEl, ref);
  }

  runEmbeddedScripts(newEl);

  doc.defaultView?.dispatchEvent(
    new CustomEvent('theme:section:load', {
      detail: { sectionId: newEl.dataset.sectionId, sectionEl: newEl },
    })
  );

  if (opts?.reveal !== false) revealSection(doc, newEl);
  return true;
}

/**
 * Smoothly scroll a section into view and briefly flash it so the user
 * notices where it landed. Idempotent — calling repeatedly during the
 * flash window just restarts the animation.
 */
export function revealSection(doc: Document, el: HTMLElement) {
  ensureRevealStyles(doc);
  // Wait a tick so the DOM has actually inserted the element and the
  // theme's CSS has applied a height, otherwise scrollIntoView lands at 0.
  requestAnimationFrame(() => {
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      el.scrollIntoView();
    }
    el.classList.remove('lc-just-added');
    // Force reflow so re-adding the class restarts the animation
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetWidth;
    el.classList.add('lc-just-added');
    setTimeout(() => el.classList.remove('lc-just-added'), 1400);
  });
}

const REVEAL_STYLE_ID = 'lc-reveal-style';

function ensureRevealStyles(doc: Document) {
  if (doc.getElementById(REVEAL_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = REVEAL_STYLE_ID;
  style.textContent = `
    @keyframes lc-flash-new {
      0%   { box-shadow: inset 0 0 0 3px rgba(0,91,211,0.9); }
      40%  { box-shadow: inset 0 0 0 3px rgba(0,91,211,0.55); }
      100% { box-shadow: inset 0 0 0 3px rgba(0,91,211,0); }
    }
    [data-section-id].lc-just-added {
      animation: lc-flash-new 1.4s ease-out;
      position: relative;
    }
  `;
  doc.head.appendChild(style);
}

/**
 * Find the outer section wrapper by id. Prefers `[data-section-id]` because
 * some themes emit a nested `<section id="…">` with the same id as the
 * outer wrapper — querySelector by `#` would return the inner one on
 * some document orders.
 */
function findSectionEl(doc: Document, sectionId: string): HTMLElement | null {
  const escaped = sectionId.replace(/"/g, '\\"');
  return (
    (doc.querySelector(`[data-section-id="${escaped}"]`) as HTMLElement | null) ||
    (doc.getElementById(`section-${sectionId}`) as HTMLElement | null)
  );
}

export function removeSectionFromDom(doc: Document, sectionId: string): boolean {
  const el = findSectionEl(doc, sectionId);
  if (!el) return false;
  doc.defaultView?.dispatchEvent(
    new CustomEvent('theme:section:unload', { detail: { sectionId } })
  );
  el.remove();
  return true;
}

export function moveSectionInDom(
  doc: Document,
  sectionId: string,
  group: string,
  newIndex: number
): boolean {
  const el = findSectionEl(doc, sectionId);
  if (!el) return false;
  const siblings = Array.from(
    doc.querySelectorAll(`[data-section-group="${group}"]`)
  ) as HTMLElement[];

  const parent = el.parentNode;
  if (!parent) return false;

  // Remove then reinsert at target index. We refetch siblings after removal.
  el.remove();
  const remaining = siblings.filter((s) => s !== el);
  if (newIndex >= remaining.length) {
    const last = remaining[remaining.length - 1];
    last?.parentNode?.insertBefore(el, last.nextSibling);
  } else {
    const ref = remaining[newIndex];
    ref?.parentNode?.insertBefore(el, ref);
  }

  doc.defaultView?.dispatchEvent(
    new CustomEvent('theme:section:reorder', {
      detail: { sectionId, newIndex },
    })
  );
  return true;
}

/**
 * Re-executes <script> tags inside a freshly-inserted DOM fragment.
 * innerHTML does not run scripts, so we clone & re-append each one.
 *
 * Also patches `document.addEventListener('DOMContentLoaded', fn)`
 * while the scripts run: the inserted code almost always wraps its
 * init in DOMContentLoaded, but by the time a section is re-rendered
 * the page has long passed that event — so without this shim the
 * listener silently never fires. With it, handlers registered during
 * a re-render run synchronously (as if DOM was already ready), which
 * is exactly what the theme author expected. The original listener
 * API is restored as soon as the scripts finish, so this doesn't
 * leak into later page behaviour.
 */
function runEmbeddedScripts(root: HTMLElement) {
  const doc = root.ownerDocument;
  const scripts = root.querySelectorAll('script');
  if (scripts.length === 0) return;

  const domReady =
    doc.readyState === 'complete' || doc.readyState === 'interactive';
  const originalAdd = doc.addEventListener.bind(doc);

  if (domReady) {
    (doc as any).addEventListener = function patched(
      event: string,
      handler: any,
      options?: any
    ) {
      if (
        event === 'DOMContentLoaded' &&
        (typeof handler === 'function' ||
          (handler && typeof handler.handleEvent === 'function'))
      ) {
        // Fire now, micro-task-deferred so the registering script
        // finishes any same-tick setup first.
        queueMicrotask(() => {
          try {
            if (typeof handler === 'function') handler(new Event('DOMContentLoaded'));
            else handler.handleEvent(new Event('DOMContentLoaded'));
          } catch (err) {
            // Don't let a bad handler kill sibling scripts.
            // eslint-disable-next-line no-console
            console.error('[preview] DOMContentLoaded handler failed', err);
          }
        });
        return;
      }
      return originalAdd(event, handler, options);
    };
  }

  try {
    scripts.forEach((oldScript) => {
      const newScript = doc.createElement('script');
      for (const attr of Array.from(oldScript.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      // Wrap inline (non-external, non-module) scripts in an IIFE so that
      // top-level `const` / `let` / `class` don't become script-scoped globals
      // that collide on re-execution. A theme that re-renders a section
      // after editing config will re-run this script, and
      //   const backdrop = ...
      // would otherwise throw "Identifier 'backdrop' has already been declared".
      //
      // Scripts that intentionally leak must use `window.x = ...`.
      // External scripts (`src=...`) and module scripts are left alone.
      const hasSrc = !!oldScript.getAttribute('src');
      const typeAttr = (oldScript.getAttribute('type') || '').toLowerCase();
      const isModule = typeAttr === 'module';
      const isPlainJs =
        !hasSrc && (typeAttr === '' || typeAttr === 'text/javascript' || typeAttr === 'application/javascript');
      const body = oldScript.text;
      if (isPlainJs && body && body.trim().length > 0 && !isModule) {
        // Single-statement IIFE; no sourcemap/line-number juggling needed.
        newScript.text = '(function(){\n' + body + '\n}).call(window);';
      } else {
        newScript.text = body;
      }
      oldScript.replaceWith(newScript);
    });
  } finally {
    // Let the microtasks registered above run with the patched
    // listener API, THEN restore. queueMicrotask here ensures all
    // the scripts' synchronous addEventListener calls have executed
    // against our shim before we put the original back.
    if (domReady) {
      queueMicrotask(() => {
        (doc as any).addEventListener = originalAdd;
      });
    }
  }
}
